import { useState, useEffect, useRef, useContext, createContext } from 'react';
import { chatService } from '../services/chat.service';
import { chatStorage } from '../utils/chatStorage';
import { config } from '../assets/config/config'
import { AuthContext } from '../context/AuthContext'
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { flushSync } from 'react-dom';

const apiUrl = config.api.url

// Create the Chat Context
const ChatContext = createContext();

// Custom hook to use the chat context
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

// Chat Provider Component
export const ChatProvider = ({ children }) => {
  const { accessToken } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [doesLocalChatNeedDbUpdate, setDoesLocalChatNeedDbUpdate] = useState(false);
  const [isSavingChat, setIsSavingChat] = useState(false);
  
  // File management state
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [citations, setCitations] = useState([]);
  
  const abortControllerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const accumulatedContentRef = useRef('');
  const displayedContentRef = useRef('');
  const lastCharIndexRef = useRef(0);
  const thinkingTextRef = useRef('');
  const isThinkingRef = useRef(false);    

  // const lastUpdateRef = useRef(Date.now());

  // Function to save chat and reload history
  const saveChatAndReloadHistory = async (chatToSave) => {
    try {
      setIsSavingChat(true);
      const savedChat = await chatService.saveFullChat(chatToSave);
      setDoesLocalChatNeedDbUpdate(false);
      
      // Reload chat history after saving
      await loadChatHistory();
      
      return savedChat;
    } catch (err) {
      console.error("Failed to save chat:", err);
      throw err;
    } finally {
      setIsSavingChat(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadChatHistory();
    loadCurrentChat();
    loadFiles();
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  const loadChatHistory = async () => {
    try {
      // setLoading(true);
      const response = await chatService.getChatHistory();
      
      // Handle different response structures
      let chats = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          chats = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          chats = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          chats = response.data.data;
        }
      } else if (Array.isArray(response)) {
        chats = response;
      }
      // Sort chats by updatedAt field in descending order (newest first)
      chats = chats.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.createdAt);
        return dateB - dateA;
      });
      setChatHistory(chats);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError(err.message);
      // Set empty array as fallback
      setChatHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentChat = () => {
    const localChat = chatStorage.getCurrentChat();
    if (localChat) {
      setCurrentChat(localChat);
      
      // Save the chat's model to localStorage if available
      if (localChat.model) {
        chatStorage.setCurrentModel(localChat.model);
      }
      
      setDoesLocalChatNeedDbUpdate(true);
    } else {
      // Start with a new chat
      const newChat = chatStorage.createNewChat();
      setCurrentChat(newChat);
      
      // Save the new chat's model to localStorage
      if (newChat.model) {
        chatStorage.setCurrentModel(newChat.model);
      }
      
      setDoesLocalChatNeedDbUpdate(false);
    }
  };

  // File management functions
  const loadFiles = async () => {
    try {
      setLoadingFiles(true);
      const response = await chatService.getUserFiles();
      setFiles(response.data.files || []);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('Failed to load files');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const uploadFile = async (file) => {
    try {
      const response = await chatService.uploadFile(file);
      const uploadedFile = response.data.file;
      // setFiles(prev => [uploadedFile, ...prev]);
      return uploadedFile;
    } catch (err) {
      console.error('Failed to upload file:', err);
      throw err;
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await chatService.deleteFile(fileId);
      // setFiles(prev => prev.filter(file => file.id !== fileId));
    } catch (err) {
      console.error('Failed to delete file:', err);
      throw err;
    }
  };

  // Send message with file context
  const sendMessageWithFileContext = async (message) => {
    // Create a new chat if none exists
    if (!currentChat) {
      const newChat = chatStorage.createNewChat("You are a helpful assistant answering based only on the provided context. Use the context to answer the user's question truthfully and concisely. If the answer cannot be found in the context, respond with 'I couldn't find that information in the provided data.' Do not make up any facts or assume anything outside the context");
      setCurrentChat(newChat);
      chatStorage.saveCurrentChat(newChat);
    }

    const chatToUse = currentChat || chatStorage.createNewChat("You are a helpful assistant answering based only on the provided context. Use the context to answer the user's question truthfully and concisely. If the answer cannot be found in the context, respond with 'I couldn't find that information in the provided data.' Do not make up any facts or assume anything outside the context");

    try {
      setMessageLoading(true);
      setError(null);
      setCitations([]);
      
      // Create user message
      const userMessage = {
        role: 'user',
        content: message,
        timestamp: new Date()
      };

      // Update local chat with user message
      const updatedMessages = [...(chatToUse.messages || []), userMessage];
      
      // If this is the first message, use it as the title
      const isFirstMessage = (chatToUse.messages || []).length === 0;
      const title = isFirstMessage 
        ? message.length > 50 
          ? `${message.substring(0, 50)}...` 
          : message
        : chatToUse.title || 'Chat with Notes';
      
      const updatedChat = {
        ...chatToUse,
        title: title,
        messages: updatedMessages,
        updatedAt: new Date(),
        model: 'gpt-4.1'
      };

      setCurrentChat(updatedChat);
      chatStorage.saveCurrentChat(updatedChat);
      setDoesLocalChatNeedDbUpdate(true);

      // Send message with file context
      // const messages = [{ role: 'user', content: message }];
      const response = await chatService.sendMessageWithFileContext(updatedMessages);

      console.log("RESPONSE RECEIVED", response)
      
      // Create assistant message
      const assistantMessage = {
        role: 'assistant',
        content: response.data.data?.content || response.data.content || response.data.answer || 'No response received',
        timestamp: new Date(),
        model: 'gpt-4.1'
      };

      // Update chat with assistant message
      const finalMessages = [...updatedMessages, assistantMessage];
      const finalChat = {
        ...updatedChat,
        messages: finalMessages,
        updatedAt: new Date()
      };

      setCurrentChat(finalChat);
      chatStorage.saveCurrentChat(finalChat);
      setDoesLocalChatNeedDbUpdate(true);

      // Update citations if available
      if (response.data.data?.citations || response.data.citations) {
        setCitations(response.data.data?.citations || response.data.citations);
      }

      return response;
    } catch (err) {
      console.error('Failed to send message with file context:', err);
      setError(err.message);
      throw err;
    } finally {
      setMessageLoading(false);
    }
  };

  const sendMessage = async (message, model, stream = true) => {
    if (!currentChat) return;

    try {
      setMessageLoading(true);
      setError(null);
      setIsStreaming(stream);
      setStreamingMessage('');
      setThinkingText('');
      setIsThinking(false);
      thinkingTextRef.current = '';
      isThinkingRef.current = false;
      
      // Reset animation state
      displayedContentRef.current = '';
      lastCharIndexRef.current = 0;

      // Create user message
      const userMessage = {
        role: 'user',
        content: message,
        timestamp: new Date()
      };

      // Update local chat with user message
      const updatedMessages = [...currentChat.messages, userMessage];
      
      // If this is the first message, use it as the title
      const isFirstMessage = currentChat.messages.length === 0;
      const title = isFirstMessage 
        ? message.length > 50 
          ? `${message.substring(0, 50)}...` 
          : message
        : currentChat.title;
      
      const updatedChat = {
        ...currentChat,
        title: title,
        messages: updatedMessages,
        updatedAt: new Date(),
        model: model
      };

      // Save the model to localStorage
      chatStorage.setCurrentModel(model);

      setCurrentChat(updatedChat);
      chatStorage.saveCurrentChat(updatedChat);
      setDoesLocalChatNeedDbUpdate(true);

      if (stream) {
        // Streaming request
        await sendStreamingMessage(updatedMessages, model, updatedChat);
      } else {
        // Non-streaming request (fallback)
        await sendNonStreamingMessage(updatedMessages, model, updatedChat);
      }

    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message);
    } finally {
      // Only set messageLoading to false
      setMessageLoading(false);
      
      // Don't set isStreaming to false here
      // Let the animation finish first, and it will be set to false
      // when the animation is complete
    }
  };

  const sendStreamingMessage = async (messages, model, chatToUpdate) => {
      /* ───── abort handling ───── */
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const ctrl = new AbortController();
      abortControllerRef.current = ctrl;
    
      /* ───── streaming state ───── */
      accumulatedContentRef.current = '';
      // Add a flag to track if we've received the [DONE] signal
      const isStreamCompletedRef = { current: false };
      const assistantMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        model
      };

      // Add the assistant message to the chat immediately
      const initialMessages = [...messages, assistantMessage];
      const initialChat = {
        ...chatToUpdate,
        messages: initialMessages,
        updatedAt: new Date(),
        model
      };

      setCurrentChat(initialChat);
      chatStorage.saveCurrentChat(initialChat);
    
      /* commits the final assistant message */
      const commitFinal = () => {
        // Store the final message content in the chat
        const finalMessage = { ...assistantMessage, content: accumulatedContentRef.current };
    
        const finalChat = {
          ...chatToUpdate,
          messages: [...messages, finalMessage],
          updatedAt: new Date(),
          model
        };
    
        setCurrentChat(finalChat);
        chatStorage.saveCurrentChat(finalChat);
        setDoesLocalChatNeedDbUpdate(true);
        
        // Instead of canceling animation, let it finish naturally
        // We'll create a completion flag to track when we're done receiving data
        
        // We don't reset the animation state here - let it complete first
      };
    
      try {
        await fetchEventSource(`${apiUrl}/v1/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken.token}`
          },
          body: JSON.stringify({
            model,
            messages: messages.map(({ role, content }) => ({ role, content })),
            stream: true
          }),
          signal: ctrl.signal,
    
          /* 1️⃣ validate first response */
          onopen(res) {
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          },

    
          /* 2️⃣ handle each data line */
          onmessage(ev) {
            let data = ev.data;
            if (data === '[DONE]') {
              // Mark that we're done receiving data
              commitFinal();
              
              // Set our shared flag to indicate stream is complete
              isStreamCompletedRef.current = true;
              
              // We need to abort the controller to stop the fetch
              // but we'll let the animation continue
              ctrl.abort();
              return;                // stops fetchEventSource promise
            }
            const { content } = JSON.parse(data);
            data = content;

            /* Process thinking tags and update UI */
            
            // Check for thinking tags
            if (data.includes('<think>')) {

              isThinkingRef.current = true;
              setIsThinking(thinkingTextRef.current);
              
              const thinkMatch = data.match(/<think>(.*)/);
              if (thinkMatch && thinkMatch[1] !== undefined) {
                const afterThink = thinkMatch[1];
                thinkingTextRef.current += afterThink;
                setThinkingText(thinkingTextRef.current);
              }
            } else if (data.includes('</think>')) {

              isThinkingRef.current = false;
              setIsThinking(thinkingTextRef.current);
              
              // Use regex to get content before and after the </think> tag
              const thinkCloseRegex = /^(.*?)<\/think>(.*)$/s;
              const match = data.match(thinkCloseRegex);
              if (match) {
                // match[1] is content before </think>, match[2] is after
                thinkingTextRef.current += match[1];
                setThinkingText(thinkingTextRef.current);
                // After </think> is regular content
                accumulatedContentRef.current += match[2];
              }
              
            } else if (isThinkingRef.current) {
              thinkingTextRef.current += data;
              setThinkingText(thinkingTextRef.current);
              // Continue accumulating thinking content
            } else {
              // Regular content
              accumulatedContentRef.current += data;
            }
            
            // Start or continue the animation
            if (!animationFrameRef.current) {
              const animateText = () => {
                // If we haven't displayed all characters yet
                if (lastCharIndexRef.current < accumulatedContentRef.current.length) {
                  // Add the next character to displayed content
                  const nextIndex = Math.min(
                    lastCharIndexRef.current + 3, 
                    accumulatedContentRef.current.length
                  );
                  displayedContentRef.current = accumulatedContentRef.current.substring(0, nextIndex);
                  lastCharIndexRef.current = nextIndex;
                  
                  // Update the UI
                  setStreamingMessage(displayedContentRef.current);
                  
                  // Continue animation
                  animationFrameRef.current = requestAnimationFrame(animateText);
                } else {
                  // We've caught up with all available content
                  animationFrameRef.current = null;
                  
                  // Check if we need to set isStreaming to false
                  // This happens when we've displayed all characters AND
                  // we've received the [DONE] signal from the server
                  if (isStreamCompletedRef.current) {
                    // Now it's safe to set isStreaming to false
                    setIsStreaming(false);
                    setStreamingMessage('');
                    setIsThinking(false);
                    isThinkingRef.current = false;
                    
                    // Reset animation state for next message
                    displayedContentRef.current = '';
                    lastCharIndexRef.current = 0;
                  }
                }
              };
              
              // Start the animation
              animationFrameRef.current = requestAnimationFrame(animateText);
            }
          },
    
          /* 3️⃣ network / parsing errors */
          onerror(err) {
            throw err;               // bubbled to catch below
          }
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        throw err;
      }
    };

  const sendNonStreamingMessage = async (messages, model, chatToUpdate) => {
    const response = await chatService.sendMessage(messages, model);
    
    const assistantMessage = {
      role: 'assistant',
      content: response.content,
      timestamp: new Date(),
      model: model
    };

    const finalMessages = [...messages, assistantMessage];
    const finalChat = {
      ...chatToUpdate,
      messages: finalMessages,
      updatedAt: new Date(),
      model: model
    };

    setCurrentChat(finalChat);
    chatStorage.saveCurrentChat(finalChat);
    setDoesLocalChatNeedDbUpdate(true);
  };

  const switchToChat = async (chatId) => {
    // Save current chat if needed
    if (doesLocalChatNeedDbUpdate && currentChat) {
      try {
        saveChatAndReloadHistory(currentChat);
      } catch (err) {
        console.error("Failed to save current chat:", err);
      }
    }

    // Find chat in history - handle both id and _id fields
    const selectedChat = chatHistory.find(chat => (chat.id || chat._id) === chatId);
    if (selectedChat) {
      setCurrentChat(selectedChat);
      chatStorage.saveCurrentChat(selectedChat);
      
      // Save the chat's model to localStorage if available
      if (selectedChat.model) {
        chatStorage.setCurrentModel(selectedChat.model);
      }
      
      setDoesLocalChatNeedDbUpdate(false);
    } else {
      console.error('Chat not found with ID:', chatId);
    }
  };

  const createNewChat = async () => {
    // Save current chat if needed
    if (doesLocalChatNeedDbUpdate && currentChat) {
      try {
        saveChatAndReloadHistory(currentChat);
        // Add to history if it's a new chat
        if (currentChat.id && currentChat.id.startsWith('temp_')) {
          setChatHistory(prev => [currentChat, ...prev]);
        }
        setDoesLocalChatNeedDbUpdate(false);
      } catch (err) {
        console.error('Failed to save current chat:', err);
      }
    }

    // Create new chat
    const newChat = chatStorage.createNewChat();
    setCurrentChat(newChat);
    
    // Save the new chat's model to localStorage
    if (newChat.model) {
      chatStorage.setCurrentModel(newChat.model);
    }
    
    setDoesLocalChatNeedDbUpdate(false);
  };

  const deleteChat = async (chatId) => {
    try {
      await chatService.deleteChat(chatId);
      setChatHistory(prev => prev.filter(chat => (chat.id || chat._id) !== chatId));
      
      // If deleting current chat, create a new one
      if (currentChat && (currentChat.id || currentChat._id) === chatId) {
        const newChat = chatStorage.createNewChat();
        setCurrentChat(newChat);
        setDoesLocalChatNeedDbUpdate(false);
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
      setError(err.message);
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Reset animation state
    displayedContentRef.current = '';
    lastCharIndexRef.current = 0;
    
    setIsStreaming(false);
    setMessageLoading(false);
    setStreamingMessage('');
    setIsThinking(false);
    isThinkingRef.current = false;
  };

  const contextValue = {
    loading,
    messageLoading,
    streamingMessage,
    isStreaming,
    thinkingText,
    isThinking,
    error,
    chatHistory,
    currentChat,
    doesLocalChatNeedDbUpdate,
    isSavingChat,
    sendMessage,
    switchToChat,
    createNewChat,
    deleteChat,
    stopStreaming,
    setError,
    // File management
    files,
    loadingFiles,
    citations,
    loadFiles,
    uploadFile,
    deleteFile,
    sendMessageWithFileContext,
    setCitations
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};
